import React, { forwardRef, useImperativeHandle, useState } from 'react';
import classNames from 'classnames';
import { Button, Loader, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { ObjectValues } from 'types';
import { Field, Form } from 'react-final-form';
import TextAreaField from 'components/TextAreaField';
import { SOFT_SIGNALS, SoftSignalPayload } from 'models/Lead';

import './DealDislikeSignalModal.styles.scss';

const OPTIONS = {
  STAY: 'stay',
  WITHDRAW: 'withdraw',
} as const;

type Option = ObjectValues<typeof OPTIONS>;

type DealDislikeSignalModalProps = {
  className?: string;
  onSubmit: (values: SoftSignalPayload, isSkipPressed?: boolean) => void;
} & ModalProps;

const DealDislikeSignalModal: React.ForwardRefRenderFunction<
  ModalRef,
  DealDislikeSignalModalProps
> = (props, ref) => {
  const { className, onSubmit, ...rest } = props;

  const [choice, setChoice] = useState<Option>();
  const [isSkipPressed, setIsSkipPressed] = useState(false);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('deal-dislike-signal-modal', className);

  const { t } = useTranslation();

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t(
        !choice
          ? 'withdrawInterestQuestion'
          : choice === OPTIONS.STAY
          ? 'interestKept'
          : 'interestWithdrawn',
      )}
      {...rest}
    >
      <Form
        onSubmit={(values: SoftSignalPayload) =>
          onSubmit(
            {
              ...values,
              withdrawInterest: choice === OPTIONS.WITHDRAW,
              softSignal: SOFT_SIGNALS.DISLIKE,
            },
            isSkipPressed,
          )
        }
        render={({ submitting, handleSubmit }) => (
          <>
            {submitting && <Loader />}

            {!choice && (
              <div>
                <p>{t('withdrawInterestSubquestion')}</p>
                <div className="actions">
                  <Button
                    variant="link"
                    type="button"
                    onClick={() => setChoice(OPTIONS.STAY)}
                  >
                    {t('illStay')}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setChoice(OPTIONS.WITHDRAW)}
                  >
                    {t('withdraw')}
                  </Button>
                </div>
              </div>
            )}

            {choice && (
              <form onSubmit={handleSubmit}>
                <Field
                  name="message"
                  component={TextAreaField}
                  label={t(
                    choice === OPTIONS.STAY
                      ? 'tellUsYourThoughts'
                      : 'tellUsWhyAreYouWithdrawing',
                  )}
                  rows={4}
                />

                <div className="actions">
                  <Button
                    variant="link"
                    disabled={submitting}
                    onClick={() => {
                      setIsSkipPressed(true);
                      setTimeout(handleSubmit, 100);
                    }}
                  >
                    {t('skip')}
                  </Button>

                  <Button
                    disabled={submitting}
                    onClick={() => {
                      setIsSkipPressed(false);
                      setTimeout(handleSubmit, 100);
                    }}
                  >
                    {t('submit')}
                  </Button>
                </div>
              </form>
            )}
          </>
        )}
      />
    </Modal>
  );
};

export default forwardRef(DealDislikeSignalModal);
